import { useState, useEffect, useMemo } from "react";

interface AnimatedLogoProps {
    alt?: string;
    width?: number;
    height?: number;
    className?: string;
    style?: React.CSSProperties;
    id?: string;
    borderRadius?: string;
    border?: string;
    shouldPlay?: boolean;
    hideBackground?: boolean;
}

const AnimatedLogo = ({
    alt = "Logo",
    width = 68,
    height = 68,
    className = "",
    style = {},
    id,
    borderRadius = "50%",
    border = "2px solid #2b3034",
    shouldPlay = false,
    hideBackground = false
}: AnimatedLogoProps) => {
    // Define array of 12 icons with custom durations (in ms)
    const FRAMES = useMemo(() => [
        { src: "/img/icon/icon-001.png", duration: 1000 },
        { src: "/img/icon/icon-002.png", duration: 500 },
        { src: "/img/icon/icon-003.png", duration: 500 },
        { src: "/img/icon/icon-004.png", duration: 1000 },
        { src: "/img/icon/icon-005.png", duration: 1000 },
        { src: "/img/icon/icon-006.png", duration: 1000 },
        { src: "/img/icon/icon-007.png", duration: 1500 },
        { src: "/img/icon/icon-008.png", duration: 1000 },
        { src: "/img/icon/icon-009.png", duration: 1000 },
        { src: "/img/icon/icon-010.png", duration: 1000 },
        { src: "/img/icon/icon-011.png", duration: 1000 },
        { src: "/img/icon/icon-012.png", duration: 1000 },
    ], []);

    const [imgSrc, setImgSrc] = useState(FRAMES[0].src);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        if (shouldPlay) {
            setIsPlaying(true);
        }
    }, [shouldPlay]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;
        let timeouts: NodeJS.Timeout[] = [];

        const runAnimation = () => {
            let accumulatedTime = 0;

            FRAMES.forEach((frame) => {
                timeouts.push(setTimeout(() => {
                    setImgSrc(frame.src);
                }, accumulatedTime));
                accumulatedTime += frame.duration;
            });
        };

        if (isPlaying) {
            runAnimation();
            const totalDuration = FRAMES.reduce((acc, frame) => acc + frame.duration, 0);
            interval = setInterval(runAnimation, totalDuration);
        } else {
            setImgSrc(FRAMES[0].src);
        }

        return () => {
            if (interval) clearInterval(interval);
            timeouts.forEach(clearTimeout);
        };
    }, [isPlaying, FRAMES]);

    return (
        <div
            id={id}
            style={{
                background: hideBackground ? "transparent" : "#fbc82f",
                borderRadius: borderRadius || "",
                border: border || "",
                overflow: "hidden",
                transition: "transform 0.3s ease",
            }}
        >
            <img
                src={imgSrc}
                alt={alt}
                className={"d-block " + className}
                style={{
                    width: (isMobile ? width * 0.8 : width) + "px",
                    height: (isMobile ? height * 0.8 : height) + "px",
                    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                    ...style
                }}
            />
        </div>

    );
};

export default AnimatedLogo;
